import { Article } from "@faire/help-center-utils/articles";
import { ArticlesType } from "@faire/help-center-utils/hooks/article/useArticles";
import { StrictLocalizeFunction } from "@faire/web--source/common/localization";
import { ActiveStep } from "@faire/web--source/slate/Stepper";
import { IBrand } from "@faire/web-api--source/indigofair/data/IBrand";
import { IRetailer } from "@faire/web-api--source/indigofair/data/IRetailer";
import { ISelfServeBrandOrder } from "@faire/web-api--source/indigofair/helpcenter/ISelfServeBrandOrder";
import { ISelfServeContextShippingResponse } from "@faire/web-api--source/indigofair/helpcenter/ISelfServeContextShippingResponse";
import { ISelfServeFaireDirectDiscountsResponse } from "@faire/web-api--source/indigofair/helpcenter/ISelfServeFaireDirectDiscountsResponse";
import * as H from "history";
import { ReactNode, type JSX } from "react";

import { StepPage } from "./view/step";

export type IssueId = string;

export interface InstantAnswer {
  titleBuilder?: InstantAnswerContentBuilder;
  // Be cautious when using JSX.Element, as it does not support text truncation on mobile screens.
  descriptionBuilder?: (
    context: InstantAnswerContext
  ) => string[] | JSX.Element;
  primaryCtaBuilder?: InstantAnswerCtaBuilder;
  secondaryCtaBuilder?: InstantAnswerCtaBuilder;
  learnMoreLink?: string;
  linkTextBuilder?: InstantAnswerLinkTextBuilder;
  additionalRequiredData?: InstantAnswerRequiredDataFn[];
}

export interface InstantAnswerScenario {
  getTitle: (arg?: number) => string;
  // Be cautious when using JSX.Element, as it does not support text truncation on mobile screens.
  getDescription: (arg?: string) => string[] | JSX.Element;
}

//TODO(ben.wong): Remove undefined return type for link builder after self-serve messenger is launched
export interface InstantAnswerCTA {
  label: string;
  getLink?: (arg?: ISelfServeBrandOrder) => string | undefined;
  onClick?: (history?: H.History) => void;
}

export interface InstantAnswerContext {
  brandOrder?: ISelfServeBrandOrder;
  shippingContext?: ISelfServeContextShippingResponse;
  faireDirectDiscountsContext?: ISelfServeFaireDirectDiscountsResponse;
  brand?: IBrand;
  retailer?: IRetailer;
  brandIdvToken?: string;
  shouldDisplayCTAOfContactBrandOrRetailer?: boolean;
}

export interface InstantAnswerRequiredData {
  field: keyof InstantAnswerContext;
  data: InstantAnswerContext[keyof InstantAnswerContext];
}

export type InstantAnswerRequiredDataFn = (
  context: SelfServeContext
) => Promise<InstantAnswerRequiredData>;

export const SelfServeNodeLevels = [0, 1, 2, 3, 4, 5] as const;
export type SelfServeNodeLevel = (typeof SelfServeNodeLevels)[number];
export type SelfServeNodeLevelWithExtra = SelfServeNodeLevel | 6;

export interface SelfServeContext {
  tree: SelfServeNodes;
  level1Node?: SelfServeNode;
  level2Node?: SelfServeNode;
  level3Node?: SelfServeNode;
  level4Node?: SelfServeNode;
  level5Node?: SelfServeNode;
  brandOrders?: ISelfServeBrandOrder[];
  selectedBrandOrder?: ISelfServeBrandOrder;
}

export type ISearchableNode = Pick<
  SelfServeNode,
  "id" | "label" | "metaData" | "level"
>;

export interface SelfServeNode {
  id: IssueId;
  label?: string;
  // used for displaying title on the order summary sidebar
  sidebarTitle?: string;
  icon?: ReactNode;
  children?: IssueId[];
  articles?: Article[];
  instantAnswer?: InstantAnswer;
  level: SelfServeNodeLevel;
  isNodeVisible?: (context: SelfServeContext) => Promise<boolean>;
  additionalPeteEventData?: (context: SelfServeContext) => Promise<string>;
  // used for search
  metaData?: string[];
  assignSetting?: () => Promise<void>;
  shouldStopAddingDefaultOtherNodeToChildren?: boolean;
}

export type SelfServeNodes = Record<IssueId, SelfServeNode>;
/**
 * A function that builds an instant answer receiving a strictLocalize function
 * This is needed given the recent changes done to localization that require a component using the
 * useStrictLocalization hook to access strictLocalize
 */
export type InstantAnswerBuilderFn = (
  strictLocalize: StrictLocalizeFunction,
  article?: ArticlesType
) => InstantAnswer;

export type CtaBuilderFn = (
  context: InstantAnswerContext,
  strictLocalize: StrictLocalizeFunction,
  articles?: ArticlesType
) => InstantAnswerCTA | undefined;

export interface NavigationContext {
  currentStep: ActiveStep;
  currentPage: StepPage;
  hasLevelQueryParams: boolean;
}

type InstantAnswerContentBuilder = (
  context: InstantAnswerContext
) => string | undefined;

type InstantAnswerOnClickFunctionBuilder = (
  context: InstantAnswerContext
) => ((history?: H.History) => void) | undefined;

type InstantAnswerLinkTextBuilder = (
  context: InstantAnswerContext
) => JSX.Element | undefined;

interface InstantAnswerCtaBuilder {
  textBuilder?: InstantAnswerContentBuilder;
  linkBuilder?: InstantAnswerContentBuilder;
  onClickFunctionBuilder?: InstantAnswerOnClickFunctionBuilder;
  linkTextBuilder?: InstantAnswerLinkTextBuilder;
}

export interface QueryState {
  isLoading: boolean;
  dataUpdatedAt: number | undefined;
}
